import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Search, ShieldAlert, Coins, AlertTriangle, CheckCircle2, TrendingUp, DollarSign } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import AdminMenu from "../../components/AdminMenu";
import MainMenu from "../../components/MainMenu";

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AuditSiteWide() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");

  const load = async (s = "") => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/admin/audit/site?search=${encodeURIComponent(s)}`);
      setData(res.data);
    } catch (e) {
      console.error("audit site load failed:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(""); }, []);

  const runSearch = (e) => {
    e?.preventDefault();
    setSearch(q);
    load(q);
  };

  return (
    <div className="min-h-screen bg-divine-black text-white">
      <header className="border-b border-divine-gold/20 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
              data-testid="audit-site-back-btn"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-white font-medium tracking-widest">COMMISSION AUDIT v2</h1>
              <p className="text-white/40 text-xs">Site-wide reconciliation</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => load(search)}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
              title="Refresh"
              disabled={loading}
              data-testid="audit-site-refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <Button
              onClick={() => navigate("/plog/audit/bonus-pool")}
              variant="outline"
              size="sm"
              className="border-divine-gold/40 text-divine-gold hover:bg-divine-gold/10 hover:text-divine-gold h-9 px-3"
              data-testid="audit-bonus-pool-btn"
            >
              <Coins className="w-4 h-4 mr-1.5" />
              Bonus Pool
            </Button>
            <AdminMenu />
            <MainMenu />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Platform totals strip */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-platform-totals">
            <TotalCard
              icon={<TrendingUp className="w-4 h-4" />}
              label="Commissions PAID"
              value={dollars(data.platform.commissions_by_status?.PAID?.sum_cents)}
              sub={`${data.platform.commissions_by_status?.PAID?.count || 0} rows`}
            />
            <TotalCard
              icon={<DollarSign className="w-4 h-4" />}
              label="Stripe payouts total"
              value={dollars(data.platform.stripe_paid_cents)}
              sub="lifetime"
            />
            <TotalCard
              icon={<AlertTriangle className="w-4 h-4" />}
              label="Commissions PENDING"
              value={dollars(data.platform.commissions_by_status?.PENDING?.sum_cents)}
              sub={`${data.platform.commissions_by_status?.PENDING?.count || 0} rows`}
            />
            <TotalCard
              icon={<Coins className="w-4 h-4" />}
              label="Bonus pool balance"
              value={dollars(data.platform.bonus_pool_cents)}
              sub="net inflow − outflow"
            />
          </div>
        )}

        {/* Search bar */}
        <form onSubmit={runSearch} className="flex gap-2" data-testid="audit-site-search-form">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search user by name, email, or user_id — press Enter for full audit"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            data-testid="audit-site-search-input"
          />
          <Button type="submit" className="bg-divine-gold hover:bg-divine-gold-light text-black">
            <Search className="w-4 h-4 mr-1.5" />
            Search
          </Button>
        </form>

        {/* Matched-users list (from search) */}
        {data && search && (
          <Card className="bg-white/5 border-white/10" data-testid="audit-site-matches">
            <CardHeader className="py-3">
              <CardTitle className="text-white text-sm">
                Matches for "{search}" · {data.matched_users?.length || 0}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {(data.matched_users || []).length === 0 ? (
                <p className="text-white/40 text-sm py-4">No users matched.</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {data.matched_users.map((u) => (
                    <li key={u.user_id} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{u.name || u.email}</div>
                        <div className="text-white/40 text-xs truncate font-mono">
                          {u.user_id} · {u.email} · {u.affiliate_compensation_profile || "STANDARD"}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(`/plog/audit/user/${u.user_id}`)}
                        className="bg-divine-gold hover:bg-divine-gold-light text-black h-8"
                        data-testid={`audit-open-user-${u.user_id}`}
                      >
                        Open audit
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {/* Red-flag triage */}
        <Card className="bg-white/5 border-white/10" data-testid="audit-site-redflags">
          <CardHeader className="py-3 flex-row items-center justify-between">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-red-400" />
              Red-flag users (any failing tie-out from latest daily snapshot)
            </CardTitle>
            <span className="text-white/40 text-xs">
              Delta floor: {dollars(data?.delta_floor_cents || 50)}
            </span>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <p className="text-white/40 text-sm py-4">Loading…</p>
            ) : (data?.red_flag_users || []).length === 0 ? (
              <div className="flex items-center gap-2 text-white/50 py-4" data-testid="audit-site-redflags-empty">
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                No users with failing tie-outs. Everything reconciles cleanly.
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {data.red_flag_users.map((u) => (
                  <li key={u.user_id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full ${u.any_red ? "bg-red-400" : "bg-amber-400"}`}
                        />
                        <span className="text-white text-sm">{u.user_name || u.user_email}</span>
                        <span className="text-white/40 text-xs font-mono">{u.user_id}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(u.tieouts_failing || []).map((t) => (
                          <span
                            key={t.id}
                            className={`text-[11px] px-2 py-0.5 rounded ${
                              t.classification === "red"
                                ? "bg-red-500/15 text-red-300"
                                : "bg-amber-500/15 text-amber-300"
                            }`}
                            title={t.label}
                          >
                            {t.id}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/plog/audit/user/${u.user_id}`)}
                      className="bg-divine-gold hover:bg-divine-gold-light text-black h-8 flex-shrink-0"
                      data-testid={`audit-open-user-${u.user_id}`}
                    >
                      Investigate
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TotalCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
        {icon}
        {label}
      </div>
      <div className="text-white text-xl font-semibold tracking-tight">{value}</div>
      <div className="text-white/30 text-xs mt-0.5">{sub}</div>
    </div>
  );
}
